package dev.lingvo.shared

import kotlinx.serialization.Serializable

/** A repository the installation can see. */
@Serializable
data class GitHubRepoDto(
    val owner: String,
    val name: String,
    val defaultBranch: String,
    val private: Boolean = false,
)

/** One candidate file, with what the path says it is. */
@Serializable
data class GitHubFileDto(
    val path: String,
    val format: FileFormat,
    /** Null means the path names no language, which is what makes it the source. */
    val locale: String? = null,
)

/**
 * What Lingvo thinks a repository contains, offered for correction.
 *
 * [kindEvidence] travels with [kind] on purpose. "KMP, because
 * `composeResources/values/strings.xml`" is checkable; "KMP" alone is a guess somebody accepts on
 * faith, and a wrong guess about the source locale imports every string as the wrong language and is
 * nearly unrecoverable.
 */
@Serializable
data class GitHubScanDto(
    val kind: ProjectKind,
    val kindEvidence: String? = null,
    val sourceLocale: String? = null,
    val files: List<GitHubFileDto> = emptyList(),
    /** Locales found across the paths, so the confirm step can offer them pre-ticked. */
    val locales: List<String> = emptyList(),
    /**
     * Paths that looked localizable and were deliberately left out, with the reason.
     *
     * **An empty result and a broken scan look identical without this.** Somebody whose
     * `values-night/` directory was excluded on purpose needs to see that it was a decision;
     * otherwise the only available conclusion is that Lingvo cannot read their repository.
     */
    val skipped: List<GitHubSkippedDto> = emptyList(),
    /**
     * The repository grouped the way its owner thinks about it.
     *
     * **Nobody reads their own repository as a list of file paths.** They read it as modules — the
     * widget, the iOS app, the shared Compose code — and as languages: "the watch app is English
     * only, the phone app has nine." The scan already knows both; this is the same information in
     * the shape somebody can check at a glance. [files] remains the exact list, one disclosure away,
     * because the shape you confirm should still be inspectable.
     */
    val modules: List<GitHubModuleDto> = emptyList(),
)

/** One part of a repository that ships its own strings. */
@Serializable
data class GitHubModuleDto(
    /** The directory that owns it — `composeApp`, `feature/home`, `iosApp`. Their name, not ours. */
    val id: String,
    /** "Compose Multiplatform", "Android", "Apple", "Web", "JVM". */
    val platform: String,
    /** "Compose Multiplatform · androidMain" — the platform, plus the source set when it says more. */
    val subtitle: String,
    /** Source language first, then the rest alphabetically. */
    val locales: List<String> = emptyList(),
    val fileCount: Int = 0,
    val formats: List<FileFormat> = emptyList(),
    /** Every path in this module, for the disclosure. */
    val paths: List<String> = emptyList(),
)

/** One path the scan refused, and why, in words the person reading them can act on. */
@Serializable
data class GitHubSkippedDto(val path: String, val reason: String)

/** Create a project from a repository, with whatever the person corrected. */
@Serializable
data class GitHubImportRequest(
    val owner: String,
    val repo: String,
    val ref: String,
    val projectName: String,
    val sourceLocale: String,
    val kind: ProjectKind = ProjectKind.OTHER,
    /** The paths to actually import. Everything else found is left alone. */
    val paths: List<String> = emptyList(),
    /**
     * Import into a project that already exists, instead of creating one.
     *
     * Null creates a project, which is the onboarding path. Anything else is somebody who already
     * has a project — made by hand, or by `lingvo init` — and wants their repository's strings in
     * it. Without this they had to create a *second* project for the same app, which is the wrong
     * shape for the thing they were asking for.
     */
    val projectSlug: String? = null,
)

/** What an import actually did, per file, rather than how many files were fetched. */
@Serializable
data class GitHubImportResultDto(
    val slug: String,
    val created: Boolean,
    val files: List<GitHubImportedFileDto> = emptyList(),
    val next: String,
) {
    val added: Int get() = files.sumOf { it.added }
    val updated: Int get() = files.sumOf { it.updated }
}

@Serializable
data class GitHubImportedFileDto(
    val path: String,
    val locale: String,
    val added: Int = 0,
    val updated: Int = 0,
    val unchanged: Int = 0,
    /**
     * Entries this file carried that the project has no key for, so nothing was written.
     *
     * **Its absence is how a whole failed import looked like a successful one.** A translation
     * attaches to a key the source already defines; when the source has not been imported yet — or
     * when a translation file has drifted ahead of it — every entry is skipped. Reporting only
     * added/updated/unchanged made that read as "0, 0, 0", indistinguishable from a file that was
     * already up to date.
     */
    val skipped: Int = 0,
    /** Set when this one file failed; the rest still imported. */
    val error: String? = null,
)

/**
 * Where to send somebody to install the app, with the proof that brings them back to *this*
 * workspace.
 *
 * The state is minted per person per workspace and lasts half an hour. It is what makes the
 * installation id GitHub hands back mean something — on its own that number is a public identifier
 * and authenticates nobody.
 */
@Serializable
data class GitHubInstallUrlDto(val url: String)

/**
 * The installations a workspace has already connected.
 *
 * Without this the app could only learn an installation id from the `installation_id` GitHub puts
 * on the redirect — which exists for exactly one page load. Anybody who arrived at the import page
 * any other way was told to install an app they had already installed.
 */
@Serializable
data class GitHubInstallationsDto(val installationIds: List<Long>)

/** What the return leg posts back: the installation GitHub reported, and the state we minted. */
@Serializable
data class GitHubClaimInstallationRequest(val installationId: Long, val state: String)
